import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, UserX, ShoppingBag } from 'lucide-react';

const problemCards = [
  {
    icon: MessageCircle,
    title: "Telegram / WhatsApp Channels",
    quotes: [
      '"BUY XYZ NOW 🚀 100% guaranteed return!"',
      '"Market crash tomorrow, sell everything!"',
      '"Join my VIP trading group for multibaggers."'
    ],
    color: "#22D3EE"
  },
  {
    icon: UserX,
    title: "Unverified Experts",
    quotes: [
      '"Turn ₹50k into ₹50 lakh with my strategy."',
      '"95% accurate stock signals — limited seats!"',
      '"Multibagger stocks every week."'
    ],
    color: "#F59E0B"
  },
  {
    icon: ShoppingBag,
    title: "Hidden Product Selling / Mis-selling",
    quotes: [
      '"Best tax saving plan" → Expensive insurance policy',
      '"Guaranteed returns" → Long lock-in ULIP',
      '"Top mutual fund pick" → High commission product'
    ],
    color: "#EF4444"
  }
];

const RotatingQuote = ({ quotes, color }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % quotes.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  return (
    <div className="h-16 flex items-center overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.p
          key={currentIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="text-[#94A3B8] text-sm italic leading-relaxed"
          style={{ borderLeft: `2px solid ${color}`, paddingLeft: '12px' }}
        >
          {quotes[currentIndex]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const ProblemSection = () => {
  return (
    <section className="py-20 md:py-32 relative" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Finance Today Is Full of{' '}
            <span className="text-[#EF4444]">Noise</span>
          </h2>
          <p className="text-[#94A3B8] text-base md:text-lg max-w-2xl mx-auto">
            Retail investors in India face a barrage of unreliable advice daily
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {problemCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.2,
                ease: "easeOut"
              }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-[#1E293B]/50 backdrop-blur-md border border-[#334155] rounded-2xl p-6 hover:border-opacity-70 transition-all"
              data-testid={`problem-card-${index}`}
            >
              {/* Icon */}
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ backgroundColor: `${card.color}15` }}
              >
                <card.icon size={24} style={{ color: card.color }} />
              </div>

              {/* Title */}
              <h3 className="text-[#F8FAFC] font-semibold text-lg mb-4">{card.title}</h3>

              {/* Rotating Quote */}
              <RotatingQuote quotes={card.quotes} color={card.color} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
